import Cookies from "js-cookie";
import { React, useState, useEffect, useRef } from "react";
import { IconButton, TextField, MenuItem, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";

// external
import Navbar from "../../../Components/Navbar";
import { fetchProfile } from "../../../utils/fetchProfile";
import TableHistory from "./Table/TableHistory";
import ViewModal from "./Modal/ViewModal";
import { ConfirmationModal } from "./Modal/ConfirmationModal";
import { fetchLicense } from "../../../utils/fetchLicense";

const BootstrapButton = styled(Button)({
  backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
  border: "1px solid hsl(186, 89.90%, 27.30%)",
  borderRadius: "4px",
  color: "#fff",
  fontSize: { xs: "14px", sm: "16px" },
  padding: "4px 10px",
  textTransform: "none",
  "&:hover": {
    backgroundImage: "linear-gradient(#0991a2, #057d8c)",
  },
});

function ImportProductGroup() {
  const accessToken = Cookies.get("access_token") || "";
  const [selectedCategoryUpload, setSelectedCategoryUpload] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [dataProfile, setDataProfile] = useState([]);
  const [dataImportHistory, setDataImportHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const queryParams = new URLSearchParams();
      const isPersonal = dataProfile.role_code === "A" ? "false" : "true";

      if (selectedCategoryUpload && selectedCategoryUpload !== "") {
        queryParams.append("category", selectedCategoryUpload);
      } else {
        queryParams.append("category", "assign_import_product_group,unassign_import_product_group,new_assign_import_product_group");
      }

      if (isPersonal && isPersonal !== "") {
        queryParams.append("is_personal", isPersonal);
      }

      if (page && page !== "") {
        queryParams.append("page", page + 1);
      }

      if (pageSize && pageSize !== "") {
        queryParams.append("size", pageSize);
      }

      queryParams.append("sort_by", "updated_at")
      queryParams.append("sort_order", "desc")


      const queryString = queryParams.toString();
      const apiUrl =
        `${process.env.REACT_APP_API_URL}/api/v1/process_queue` +
        (queryString ? `?${queryString}` : "");

  
      const response = await fetch(apiUrl,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const result = await response.json();
  
      if (result.status === 200) {
        const { data, pagination_info } = result;
        setDataImportHistory(
          data.map((item) => ({
            id: item.id,
            title: item.title,
            status: item.status,
            description: item.description,
            created_at: item.created_at,
            updated_at: item.updated_at,
            process_at: item.process_at,
          }))
        );
        setRowCount(pagination_info.total || 0);
      } else (
        setDataImportHistory([])
      )
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false)
  };  

  const handleRefresh = () => {
    if (!isDisabled) {
      setIsDisabled(true);
      setCountdown(10);
      fetchData(page, pageSize, selectedCategoryUpload);
  
      const intervalId = setInterval(() => {
        setCountdown(prevCount => {
          if (prevCount <= 1) {
            clearInterval(intervalId);
            setIsDisabled(false);
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleDownloadTemplateFile = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/product-group-master/product-group/export/template`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Template Product Group.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        console.error(
          "Failed to download template file. Response status:",
          response.status
        );
      }
    } catch (error) {
      console.error("Error downloading template file:", error);
    }
    setIsLoading(false);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleCategoryUploadChange = (event) => {
    setSelectedCategoryUpload(event.target.value);
  }

  const handleUpload = async ({ mode, onCloseModal }) => {
    try {
      const data = await fetchLicense();
      if (data?.is_license_expired) {
        alert(data?.license_message);
        return;
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong while checking the license");
      return;
    }
  
    setIsLoading(true);
  
    const formData = new FormData();
    formData.append("file", file);
    formData.append("mode", mode);
  
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/product-group-master/product-group/import/template/v2`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
  
      if (response.status === 201) {
        const data = await response.json();
        alert(data.detail);
        onCloseModal();
        fetchData(page, pageSize, selectedCategoryUpload);
      } else if (response.status === 200) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Product Group Error.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        alert("There is an issue in the Excel file. Please check its content!");
      } else {
        const data = await response.json();
        alert(data.detail);
      }
    } catch (error) {
      alert("Error occurred during upload.");
    } finally {
      setFile(null);
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadAssign = () =>
    handleUpload({
      mode: "assign",
      onCloseModal: handleCloseConfirmationModalAssign,
    });
  
  const handleUploadUnassign = () =>
    handleUpload({
      mode: "unassign",
      onCloseModal: handleCloseConfirmationModalUnassign,
    });
  
  const handleUploadCreateAssign = () =>
    handleUpload({
      mode: "new_assign",
      onCloseModal: handleCloseConfirmationModalCreateAssign,
    });

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState("");

  const handleOpenViewModal = (id) => {
    setSelectedHistoryId(id);
    setViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setViewModalOpen(false);
    setSelectedHistoryId("");
  };
  

  const [confirmationModalOpenAssign, setConfirmationModalOpenAssign] = useState(false);
  const [confirmationModalOpenUnassign, setConfirmationModalOpenUnassign] = useState(false);
  const [confirmationModalOpenCreateAssign, setConfirmationModalOpenCreateAssign] = useState(false);

  const handelOpenConfirmationAssign = () => {
    if (!file) {
        alert("Please select a file before uploading.");
        return;
      }

    setConfirmationModalOpenAssign(true);
  };

  const handelOpenConfirmationUnassign = () => {
    if (!file) {
        alert("Please select a file before uploading.");
        return;
      }

    setConfirmationModalOpenUnassign(true);
  };

  const handelOpenConfirmationCreateAssign = () => {
    if (!file) {
        alert("Please select a file before uploading.");
        return;
      }

    setConfirmationModalOpenCreateAssign(true);
  };

  const handleCloseConfirmationModalAssign = () => {
    setConfirmationModalOpenAssign(false);
  };
  
  const handleCloseConfirmationModalUnassign = () => {
    setConfirmationModalOpenUnassign(false);
  };
  
  
  const handleCloseConfirmationModalCreateAssign = () => {
    setConfirmationModalOpenCreateAssign(false);
  };
  
  useEffect(() => {
    const handler = setTimeout(() => {
      if (dataProfile) {
        fetchData();
      }
    }, 200);
    return () => {
      clearTimeout(handler);
    };
}, [dataProfile, page, pageSize, selectedCategoryUpload]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProfile(accessToken, setDataProfile, setNotAllowAccessPage, [
        "13",
        "14",
      ]);
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [accessToken]);

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="Upload Product Groups"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div>Not allow to access this page</div>
      </div>
    );
  }

  return (
    <div>
      <Navbar
        active="Upload Product Groups"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <div
        className="button-audit"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "10px",
          marginRight: "20px",
          marginLeft: "20px",
        }}
      >
        <div className="">
          <div style={{ marginBottom: 10 }}>
            <input
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              accept=".xlsx"
            />
          </div>
          <div style={{ display: "flex", gap: "5px" }}>
            <BootstrapButton
              onClick={handleDownloadTemplateFile}
              variant="contained"
              disableRipple
              disabled={isLoading}
              className="button-audit-plan"
            >
              <p
                style={{
                  fontSize: "15px",
                  height: "10px",
                  textAlign: "center",
                  marginTop: "1px",
                }}
              >
                {isLoading ? "Loading..." : "Template"}
              </p>
            </BootstrapButton>
            <BootstrapButton
              variant="contained"
              disableRipple
              onClick={handelOpenConfirmationAssign}
              disabled={isLoading}
            >
              <p
                style={{
                  fontSize: "15px",
                  height: "10px",
                  textAlign: "center",
                  marginTop: "1px",
                }}
              >
                {isLoading ? "Loading..." : "Assign"}
              </p>
            </BootstrapButton>
            <BootstrapButton
              variant="contained"
              disableRipple
              onClick={handelOpenConfirmationUnassign}
              disabled={isLoading}
            >
              <p
                style={{
                  fontSize: "15px",
                  height: "10px",
                  textAlign: "center",
                  marginTop: "1px",
                }}
              >
                {isLoading ? "Loading..." : "Unassign"}
              </p>
            </BootstrapButton>
            <BootstrapButton
              variant="contained"
              disableRipple
              onClick={handelOpenConfirmationCreateAssign}
              disabled={isLoading}
            >
              <p
                style={{
                  fontSize: "15px",
                  height: "10px",
                  textAlign: "center",
                  marginTop: "1px",
                }}
              >
                {isLoading ? "Loading..." : "Create & Assign New Product Group"}
              </p>
            </BootstrapButton>
          </div>
          <div>
          <TextField
              id="outlined-select-currency"
              label="History Category"
              select
              sx={{
                mr: 1,
                mt: 4,
                height: 40,
                width: 200,
              }}
              size="small"
              value={selectedCategoryUpload}
              onChange={handleCategoryUploadChange}
              InputProps={{
                endAdornment: selectedCategoryUpload && (
                  <IconButton
                    sx={{ mr: 1.5 }}
                    edge="end"
                    onClick={() => setSelectedCategoryUpload("")}
                  >
                    <CloseIcon />
                  </IconButton>
                ),
              }}
            >
                <MenuItem key="assign_import_product_group" value="assign_import_product_group">
                  Assign
                </MenuItem>
                <MenuItem key="unassign_import_product_group" value="unassign_import_product_group">
                  Unassign
                </MenuItem>
                <MenuItem key="new_assign_import_product_group" value="new_assign_import_product_group">
                  Create & Assign
                </MenuItem>
            </TextField>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ marginBottom: 10 }}>
            <BootstrapButton
              onClick={() => window.location.replace("/product-master")}
              variant="contained"
              disableRipple
              disabled={isLoading}
              className="button-audit-plan"
            >
              <p
                style={{
                  fontSize: "15px",
                  height: "10px",
                  textAlign: "center",
                  marginTop: "1px",
                }}
              >
                {isLoading ? "Loading..." : "Back To Product Master"}
              </p>
            </BootstrapButton>
          </div>
          <div>
            <BootstrapButton
              onClick={handleRefresh}
              variant="contained"
              disableRipple
              disabled={isLoading || isDisabled}
              className="button-audit-plan"
            >
              <p
                style={{
                  fontSize: "15px",
                  height: "10px",
                  textAlign: "center",
                  marginTop: "1px",
                }}
              >
                {isLoading
                  ? "Loading..."
                  : isDisabled
                  ? `Wait ${countdown}s`
                  : "Refresh Status"}
              </p>
            </BootstrapButton>
          </div>
        </div>
      </div>
      <div>
        <TableHistory
          dataImportHistory={dataImportHistory}
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          handlePageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          accessToken={accessToken}
          handleOpenViewModal={handleOpenViewModal}
          dataProfile={dataProfile}
        />
        <ViewModal 
          open={viewModalOpen} 
          onClose={handleCloseModal} 
          selectedHistoryId={selectedHistoryId}
        />
        <ConfirmationModal
          open={confirmationModalOpenAssign}
          onClose={handleCloseConfirmationModalAssign}
          textConfirmation={"Apakah anda yakin untuk melakukan upload penambahan product?"}
          handleConfirm={handleUploadAssign}
          isLoading={isLoading}
        />
        <ConfirmationModal
          open={confirmationModalOpenUnassign}
          onClose={handleCloseConfirmationModalUnassign}
          textConfirmation={"Apakah anda yakin untuk melakukan upload penghapusan product?"}
          handleConfirm={handleUploadUnassign}
          isLoading={isLoading}
        />
        <ConfirmationModal
          open={confirmationModalOpenCreateAssign}
          onClose={handleCloseConfirmationModalCreateAssign}
          textConfirmation={"Apakah anda yakin untuk melakukan upload create & penambahan product?"}
          handleConfirm={handleUploadCreateAssign}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

export default ImportProductGroup;
